import { graphql } from "gatsby";
import React from "react";
import { ArticleHeader } from "../components/article-header";
import { Buttons } from "../components/blocks/buttons";
import { CardList } from "../components/blocks/card-list";
import { IntroText } from "../components/blocks/intro-text";
import { LinksList } from "../components/blocks/links-list";
import { Quote } from "../components/blocks/quote";
import { Slider } from "../components/blocks/slider";
import { TextContent } from "../components/blocks/text-content";
import { Title } from "../components/blocks/title";
import { Layout } from "../components/layout";
import { PrettyPrint } from "../components/prettyprint";
import { DescendantList } from "../components/blocks/descendant-list";
import { Container } from "../components/container";
import { parseLink, parseSimpleLink } from "../utils/link";
import "../fragments/link";
import { Form } from "../components/blocks/form";

const ArticlePage: React.FC<{ data: Queries.ArticleQuery }> = ({ data }) => {
  const { Page_title, Content, Page_basics, Event_info, SEO_box } =
    data.strapiArticle!;
  const Page_media = Page_basics ? Page_basics.Page_media : null;
  const headerImage = Page_media
    ? Page_media.HeaderImage
      ? (Page_media.HeaderImage.localFile as ImageDataLikeWithPropsForGifs)
      : null
    : null;
  const headerVideo = Page_media ? Page_media.HeaderVideo : null;
  const introduction = Page_basics
    ? Page_basics.introduction?.data?.childrenMarkdownRemark?.map((child) => {
        if (!child || !child.html) return null;
        return (
          <div
            key={`article-intro-${child.id} `}
            dangerouslySetInnerHTML={{ __html: child.html }}
          />
        );
      })
    : null;
  const summary = Page_basics ? Page_basics.summary : null;
  const author = Page_basics
    ? Page_basics.author
      ? Page_basics.author
      : null
    : null;
  const seo = {
    title: SEO_box?.SEO_title || Page_title || undefined,
    description: SEO_box?.SEO_excerpt || summary || undefined,
    image:
      SEO_box?.SEO_image?.localFile?.publicURL ||
      Page_basics?.Page_media?.ThumbnailImage?.localFile?.publicURL ||
      undefined,
  };
  if (!data.strapiArticle) return null;
  return (
    <Layout contentId={data.strapiArticle.id} seo={seo}>
      <ArticleHeader
        title={Page_title}
        image={headerImage}
        video={headerVideo}
        eventInfo={
          Event_info
            ? {
                endDate: Event_info.End_date,
                startDate: Event_info.Start_date,
                startTime: Event_info.Start_time,
                endTime: Event_info.End_time,
                location: Event_info.Location,
                ticketLabel: Event_info.Ticket_button_label,
                ticketURL: Event_info.Ticket_button_url,
              }
            : null
        }
        introduction={introduction || null}
        author={author}
        summary={summary}
      />
      <div>
        {Content &&
          Content.map((content) => {
            if (!content) return null;
            switch (content.strapi_component) {
              case "content-blocks.intro-text": {
                const castContent =
                  content as Queries.STRAPI__COMPONENT_CONTENT_BLOCKS_INTRO_TEXT;
                if (!castContent.Content) return null;
                return (
                  <IntroText key={castContent.id}>
                    {castContent.Content.data?.childrenMarkdownRemark?.map(
                      (child) => {
                        if (!child || !child.html) return null;
                        return (
                          <div
                            key={`introText-${child.id}`}
                            dangerouslySetInnerHTML={{ __html: child.html }}
                          />
                        );
                      },
                    )}
                  </IntroText>
                );
              }
              case "content-blocks.slider": {
                const castContent =
                  content as Queries.STRAPI__COMPONENT_CONTENT_BLOCKS_SLIDER;
                if (!castContent.Item) return null;
                return (
                  <Slider
                    key={content.id}
                    items={castContent.Item.map((item) =>
                      item
                        ? {
                            id: item.id,
                            caption: item.Caption
                              ? item.Caption
                              : item.Image
                              ? item.Image.caption
                              : null,
                            image: item.Image
                              ? (item.Image
                                  .localFile as ImageDataLikeWithPropsForGifs | null)
                              : null,
                            aspectRatio: item.AspectRatio,
                            videoURL: item.VideoURL,
                            vimeoID: item.vimeo_id,
                            youtubeID: item.youtube_id,
                          }
                        : null,
                    )}
                  />
                );
              }
              case "content-blocks.text-content": {
                const castContent =
                  content as Queries.STRAPI__COMPONENT_CONTENT_BLOCKS_TEXT_CONTENT;
                if (!castContent.Content) return null;
                if (!castContent.Content.data) return null;
                return (
                  <TextContent key={castContent.id}>
                    {castContent.Content.data?.childrenMarkdownRemark?.map(
                      (child) => {
                        if (!child || !child.html) return null;
                        return (
                          <div
                            key={`text-content-${child.id}`}
                            dangerouslySetInnerHTML={{ __html: child.html }}
                          />
                        );
                      },
                    )}
                  </TextContent>
                );
              }
              case "content-blocks.quote": {
                const castContent =
                  content as Queries.STRAPI__COMPONENT_CONTENT_BLOCKS_QUOTE;
                return <Quote key={content.id}>{castContent.Quote}</Quote>;
              }
              case "content-blocks.title": {
                const castContent =
                  content as Queries.STRAPI__COMPONENT_CONTENT_BLOCKS_TITLE;
                return (
                  <Title key={content.id} title={castContent.Title || ""} />
                );
              }
              case "content-blocks.profile-entries": {
                const castContent =
                  content as Queries.STRAPI__COMPONENT_CONTENT_BLOCKS_PROFILE_ENTRIES;
                if (!castContent.Profile_cards) return null;
                return (
                  <CardList
                    title={castContent.Title}
                    items={castContent.Profile_cards.map((item) =>
                      item
                        ? {
                            id: item.id,
                            title: item.Title,
                            url: item.URL,
                            image: item.Image
                              ? (item.Image
                                  .localFile as ImageDataLikeWithPropsForGifs | null)
                              : null,
                            border: true,
                          }
                        : null,
                    )}
                  />
                );
              }
              case "content-blocks.buttons": {
                const castContent =
                  content as Queries.STRAPI__COMPONENT_CONTENT_BLOCKS_BUTTONS;
                if (!castContent.link) return null;
                return (
                  <Buttons
                    key={content.id}
                    items={castContent.link.map(parseLink)}
                  />
                );
              }
              case "content-blocks.logo-s-block": {
                const castContent =
                  content as Queries.STRAPI__COMPONENT_CONTENT_BLOCKS_LOGO_S_BLOCK;
                return (
                  <CardList
                    columns={5}
                    title={castContent.Logo_block_title || undefined}
                    items={
                      castContent.Logo_blocks
                        ? castContent.Logo_blocks.map((item) =>
                            item
                              ? {
                                  id: item.id,
                                  url: item.URL,
                                  image: item.Image
                                    ? (item.Image
                                        .localFile as ImageDataLikeWithPropsForGifs | null)
                                    : null,
                                  border: false,
                                  background: false,
                                  objectFit: "contain",
                                }
                              : null,
                          )
                        : []
                    }
                  />
                );
              }
              case "content-blocks.call-to-action-link-blocks": {
                const castContent =
                  content as Queries.STRAPI__COMPONENT_CONTENT_BLOCKS_CALL_TO_ACTION_LINK_BLOCKS;
                return (
                  <CardList
                    title={castContent.CTA_block_title}
                    items={
                      castContent.CTA_link_block
                        ? castContent.CTA_link_block.map(parseLink)
                        : []
                    }
                  />
                );
              }
              case "content-blocks.descendant-list": {
                const castContent =
                  content as Queries.STRAPI__COMPONENT_CONTENT_BLOCKS_DESCENDANT_LIST;
                if (!data.strapiArticle?.id) return null;
                return (
                  <DescendantList
                    articleId={data.strapiArticle?.id}
                    title={castContent.title}
                    directChildrenOnly={!!castContent.direct_children_only}
                  />
                );
              }
              case "content-blocks.simple-link-list": {
                const castContent =
                  content as Queries.STRAPI__COMPONENT_CONTENT_BLOCKS_SIMPLE_LINK_LIST;
                if (!castContent.links) return null;
                return (
                  <LinksList
                    key={content.id}
                    items={castContent.links.map(parseSimpleLink)}
                  />
                );
              }
              case "content-blocks.form": {
                const castContent =
                  content as Queries.STRAPI__COMPONENT_CONTENT_BLOCKS_FORM;
                const { form } = castContent;
                if (!form) return null;
                return (
                  <Form
                    key={content.id}
                    {...{
                      id: content.id,
                      hasCancelButton: form.has_cancel_button,
                      cancelButtonText: form.cancel_button_text,
                      headerAuthKey: form.header_auth_key,
                      heatherAuthToken: form.heather_auth_token,
                      name: form.name,
                      postURL: form.post_url,
                      recipient: form.recipient,
                      submitButtonText: form.submit_button_text,
                      successMessageOverride: form.success_message_override,
                      errorMessageOverride: form.error_message_override,
                      formElements: form.form_elements
                        ? form.form_elements.map((element) =>
                            element
                              ? {
                                  altText: element.alt_text,
                                  id: element.id,
                                  label: element.label,
                                  name: element.name,
                                  type: element.type,
                                  placeholder: element.placeholder,
                                  tooltip: element.tooltip,
                                  defaultValue: element.default_value,
                                  required: element.required,
                                }
                              : null,
                          )
                        : null,
                    }}
                  />
                );
              }
              default:
                return (
                  <Container key={content.id}>
                    Unknown content block {content.strapi_component}
                    <PrettyPrint obj={content} />
                  </Container>
                );
            }
          })}
      </div>
      {data.strapiArticle.keywords && !!data.strapiArticle.keywords.length && (
        <Container className="mt-4 grid-cols-4 gap-16 lg:grid">
          <div className="col-span-1"></div>
          <div className="col-span-2 text-beige">
            <span className="font-bold">Tags: </span>
            {data.strapiArticle.keywords?.map((item, i) =>
              item ? (
                <span key={`keyword-${i}`}>
                  <a
                    href={`/search/?q=${encodeURIComponent(
                      item.keyword || "",
                    )}`}
                    className="underline"
                  >
                    {item.keyword}
                  </a>
                  {data.strapiArticle?.keywords?.length &&
                    i < data.strapiArticle?.keywords?.length - 1 &&
                    ", "}
                </span>
              ) : null,
            )}
          </div>
        </Container>
      )}
    </Layout>
  );
};

export default ArticlePage;

export const pageQuery = graphql`
  query Article($slug: String) {
    strapiArticle(slug: { eq: $slug }) {
      id
      slug
      Page_title
      parent_article {
        id
      }
      sub_category {
        id
      }
      SEO_box {
        SEO_excerpt
        SEO_image {
          localFile {
            publicURL
          }
        }
        SEO_title
      }
      Event_info {
        Start_date(formatString: "D MMMM YYYY", locale: "en")
        End_date(formatString: "D MMMM YYYY", locale: "en")
        Start_time
        End_time
        Location
        Ticket_button_label
        Ticket_button_url
      }
      article_type {
        name
      }
      keywords {
        keyword
      }
      Page_basics {
        Page_media {
          HeaderVideo
          ThumbnailVideo
          HeaderImage {
            localFile {
              extension
              publicURL
              childImageSharp {
                gatsbyImageData(
                  width: 1152
                  placeholder: BLURRED
                  formats: [AUTO, WEBP, AVIF]
                )
              }
            }
          }
          ThumbnailImage {
            localFile {
              extension
              publicURL
              childImageSharp {
                gatsbyImageData(
                  width: 600
                  placeholder: BLURRED
                  formats: [AUTO, WEBP, AVIF]
                )
              }
            }
          }
        }
        summary
        introduction {
          data {
            childrenMarkdownRemark {
              id
              html
            }
          }
        }
        author {
          name
          slug
        }
      }
      keywords {
        keyword
      }
      Content {
        ... on STRAPI__COMPONENT_CONTENT_BLOCKS_BUTTONS {
          id
          link {
            ...Link
          }
          strapi_component
        }
        ... on STRAPI__COMPONENT_CONTENT_BLOCKS_CALL_TO_ACTION_LINK_BLOCKS {
          id
          CTA_block_title
          CTA_link_block {
            ...Link
          }
          strapi_component
        }
        ... on STRAPI__COMPONENT_CONTENT_BLOCKS_INTRO_TEXT {
          id
          Content {
            data {
              childrenMarkdownRemark {
                html
              }
            }
          }
          strapi_component
        }
        ... on STRAPI__COMPONENT_CONTENT_BLOCKS_LOGO_S_BLOCK {
          id
          Logo_block_title
          Logo_blocks {
            URL
            Image {
              localFile {
                extension
                publicURL
                childImageSharp {
                  gatsbyImageData(
                    width: 600
                    placeholder: BLURRED
                    formats: [AUTO, WEBP, AVIF]
                  )
                }
              }
            }
          }
          strapi_component
        }
        ... on STRAPI__COMPONENT_CONTENT_BLOCKS_PROFILE_ENTRIES {
          id
          Profile_cards {
            Email
            Title
            URL
            Image {
              localFile {
                extension
                publicURL
                childImageSharp {
                  gatsbyImageData(
                    width: 600
                    placeholder: BLURRED
                    formats: [AUTO, WEBP, AVIF]
                  )
                }
              }
            }
          }
          Title
          strapi_component
        }
        ... on STRAPI__COMPONENT_CONTENT_BLOCKS_QUOTE {
          id
          Quote
          strapi_component
        }
        ... on STRAPI__COMPONENT_CONTENT_BLOCKS_SLIDER {
          id
          Item {
            id
            Caption
            vimeo_id
            youtube_id
            AspectRatio
            VideoURL
            Image {
              caption
              localFile {
                extension
                publicURL
                childImageSharp {
                  gatsbyImageData(
                    width: 1152
                    placeholder: BLURRED
                    formats: [AUTO, WEBP, AVIF]
                  )
                }
              }
            }
          }
          strapi_component
        }
        ... on STRAPI__COMPONENT_CONTENT_BLOCKS_TEXT_CONTENT {
          id
          Content {
            data {
              childrenMarkdownRemark {
                html
              }
            }
          }
          strapi_component
        }
        ... on STRAPI__COMPONENT_CONTENT_BLOCKS_TITLE {
          id
          Title
          strapi_component
        }
        ... on STRAPI__COMPONENT_CONTENT_BLOCKS_DESCENDANT_LIST {
          id
          direct_children_only
          title
          strapi_component
        }
        ... on STRAPI__COMPONENT_CONTENT_BLOCKS_SIMPLE_LINK_LIST {
          id
          strapi_component
          links {
            text
            sub_text
            external_url
            type
            article {
              Page_title
              id
              slug
              Page_basics {
                summary
              }
              Event_info {
                Start_date(formatString: "D MMM YY", locale: "en")
              }
              article_type {
                name
              }
            }
            artist {
              artistUrl
              name
            }
            artwork {
              artworkUrl
              title
              artist {
                name
              }
              length
              year
            }
            sub_category {
              name
              slug
            }
          }
        }
        ... on STRAPI__COMPONENT_CONTENT_BLOCKS_FORM {
          id
          strapi_component
          form {
            cancel_button_text
            has_cancel_button
            header_auth_key
            heather_auth_token
            name
            post_url
            recipient
            submit_button_text
            success_message_override
            error_message_override
            form_elements {
              alt_text
              id
              label
              name
              type
              default_value
              placeholder
              tooltip
              required
            }
          }
        }
      }
    }
  }
`;
